import React, { useState, useCallback, useMemo } from 'react';
import { useGraphService } from '../GraphContext';
import MappingSelect from '../../../Shared/Components/MappingSelect';
import Style from './CustomAttributesPanel.module.css';

const EMPTY_OPTIONS = { erp: [], ecom: [] };
const EMPTY_SET = new Set();
// Saved values can arrive as numbers or null from the API; compare them as text
const asText = (value) => (value === null || value === undefined ? '' : String(value));

// ---------------------------------------------------------------------------
// CustomAttributesPanel
//
// Adds attribute pairs to local state immediately so nodes + auto-edges
// appear on the canvas right away. The actual API save happens when the
// user clicks the canvas "Save" button (handled in GraphContext.saveState).
// Shipping / Payment / Sales Person pick each side from a dropdown of the values
// that system offers, whose last option switches to a text field for a custom value.
//
// Props:
//   type: 'shipping' | 'payment' | 'salesperson' | 'custom'  (default: 'custom')
// ---------------------------------------------------------------------------
const CustomAttributesPanel = ({ type = 'custom' }) => {
  const {
    shippingMappings, setShippingMappings,
    paymentMappings, setPaymentMappings,
    salesPersonMappings, setSalesPersonMappings,
    shippingOptions, paymentOptions, salesPersonOptions,
    removedShippingIndices, removedPaymentIndices, removedSalesPersonIndices,
    customAttributes, addCustomAttribute,
  } = useGraphService();

  const [isOpen, setIsOpen] = useState(false);
  const [erpValue, setErpValue] = useState('');
  const [ecomValue, setEcomValue] = useState('');
  const [error, setError] = useState('');
  // Bumped after Add / Close so the dropdowns leave custom-text mode and start fresh
  const [formKey, setFormKey] = useState(0);

  const isSalesPerson = type === 'salesperson';
  const isApiType = type === 'shipping' || type === 'payment' || isSalesPerson;
  const mappings = useMemo(() => (
    type === 'shipping' ? (shippingMappings || [])
      : type === 'payment' ? (paymentMappings || [])
      : type === 'salesperson' ? (salesPersonMappings || [])
      : (customAttributes || [])
  ), [type, shippingMappings, paymentMappings, salesPersonMappings, customAttributes]);
  const setMappings = type === 'shipping' ? setShippingMappings
    : isSalesPerson ? setSalesPersonMappings
    : setPaymentMappings;
  // A mapping whose edge was deleted on the canvas stays in `mappings` (indices must stay
  // stable until Save) but no longer counts, so its values can be mapped again right away.
  const removedIndices = (
    type === 'shipping' ? removedShippingIndices
      : type === 'payment' ? removedPaymentIndices
      : isSalesPerson ? removedSalesPersonIndices
      : null
  ) || EMPTY_SET;
  const activeMappings = useMemo(
    () => mappings.filter((_, index) => !removedIndices.has(index)),
    [mappings, removedIndices],
  );

  // Dropdown options: the values each system offers ({ erp, ecom } lists of { value, label })
  const valueName = isSalesPerson ? 'sales person' : 'method';
  const options = (
    type === 'shipping' ? shippingOptions
      : type === 'payment' ? paymentOptions
      : isSalesPerson ? salesPersonOptions
      : null
  ) || EMPTY_OPTIONS;
  // An ERP sales person maps to a single eCommerce one, so already-mapped codes are not offered
  const erpOptions = useMemo(() => (
    isSalesPerson
      ? options.erp.filter((option) => !activeMappings.some((m) => asText(m.erpSalesPersonCode) === option.value))
      : options.erp
  ), [isSalesPerson, options, activeMappings]);
  const ecomOptions = options.ecom;

  const handleAdd = useCallback(() => {
    const trimmedErp  = erpValue.trim();
    const trimmedEcom = ecomValue.trim();

    if (!trimmedErp || !trimmedEcom) {
      setError('Both fields are required.');
      return;
    }

    const erpKey  = isSalesPerson ? 'erpSalesPersonCode'       : isApiType ? 'erpMethod'       : 'erpField';
    const ecomKey = isSalesPerson ? 'ecommerceSalesPersonCode' : isApiType ? 'ecommerceMethod' : 'ecomField';

    // An ERP sales person maps to a single eCommerce one (same rule as the Sales Person tab)
    if (isSalesPerson && activeMappings.some((m) => asText(m[erpKey]) === trimmedErp)) {
      setError(`"${trimmedErp}" is already mapped.`);
      return;
    }

    const isDuplicate = activeMappings.some(
      (m) => asText(m[erpKey]) === trimmedErp && asText(m[ecomKey]) === trimmedEcom,
    );
    if (isDuplicate) {
      setError('This mapping already exists.');
      return;
    }

    if (!isApiType) {
      // Custom attributes: save to localStorage via context helper
      addCustomAttribute(trimmedErp, trimmedEcom);
    } else if (isSalesPerson) {
      // Custom codes have no system ID; the backend receives empty IDs for them.
      setMappings([...mappings, {
        erpSalesPersonId: '',
        erpSalesPersonCode: trimmedErp,
        ecommerceSalesPersonId: '',
        ecommerceSalesPersonCode: trimmedEcom,
      }]);
    } else {
      // Shipping / Payment: update state immediately so canvas nodes + auto-edges appear.
      // API save happens when the user clicks the canvas Save button.
      const newEntry = {
        erpMethod: trimmedErp,
        ecommerceMethod: trimmedEcom,
        isEcommerceDefault: false,
        isErpDefault: false,
      };
      setMappings([...mappings, newEntry]);
    }

    setErpValue('');
    setEcomValue('');
    setError('');
    setFormKey((key) => key + 1);
    setIsOpen(false);
  }, [erpValue, ecomValue, mappings, activeMappings, isApiType, isSalesPerson, addCustomAttribute, setMappings]);

  const handleKeyDown = useCallback((e) => { if (e.key === 'Enter') handleAdd(); }, [handleAdd]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setErpValue('');
    setEcomValue('');
    setError('');
    setFormKey((key) => key + 1);
  }, []);

  return (
    <div className={Style.root}>
      {/* Trigger button - always visible when closed */}
      {!isOpen && (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className={`btn ${Style.trigger}`}
        >
          <i className="uil-plus" aria-hidden="true" />
          Add Custom Attributes
        </button>
      )}

      {/* Inline expanding form - slides in to the right */}
      <div
        className={Style.form}
        style={{ maxWidth: isOpen ? '700px' : '0px', opacity: isOpen ? 1 : 0 }}
      >
        {isApiType ? (
          <>
            {/* Values each system offers; the last option switches to a text field for a custom value.
                The wrapper catches Enter from that text field. */}
            <div className={Style.select} onKeyDown={handleKeyDown}>
              <MappingSelect
                key={`erp-${formKey}`}
                inputId={`custom-attribute-erp-${type}`}
                options={erpOptions}
                value={erpValue}
                placeholder={`Select ERP ${valueName}`}
                customLabel="+ Custom attribute"
                customPlaceholder={`Type ERP ${valueName}`}
                hasError={error !== '' && erpValue.trim() === ''}
                onChange={(value) => { setErpValue(value); setError(''); }}
              />
            </div>
            <div className={Style.select} onKeyDown={handleKeyDown}>
              <MappingSelect
                key={`ecom-${formKey}`}
                inputId={`custom-attribute-ecom-${type}`}
                options={ecomOptions}
                value={ecomValue}
                placeholder={`Select eCommerce ${valueName}`}
                customLabel="+ Custom attribute"
                customPlaceholder={`Type eCommerce ${valueName}`}
                hasError={error !== '' && ecomValue.trim() === ''}
                onChange={(value) => { setEcomValue(value); setError(''); }}
              />
            </div>
          </>
        ) : (
          <>
            <input
              value={erpValue}
              onChange={(e) => { setErpValue(e.target.value); setError(''); }}
              onKeyDown={handleKeyDown}
              placeholder="ERP method"
              className={`form-control form-control-sm ${Style.input}`}
            />

            <input
              value={ecomValue}
              onChange={(e) => { setEcomValue(e.target.value); setError(''); }}
              onKeyDown={handleKeyDown}
              placeholder="eCommerce method"
              className={`form-control form-control-sm ${Style.input}`}
            />
          </>
        )}

        <button
          type="button"
          onClick={handleAdd}
          className={Style.addButton}
        >
          Add
        </button>

        <button
          type="button"
          onClick={handleClose}
          className={Style.closeButton}
          aria-label="Close"
        >
          <i className="uil-times" aria-hidden="true" />
        </button>
      </div>

      {/* Inline error */}
      {isOpen && error && (
        <span className={Style.error}>{error}</span>
      )}
    </div>
  );
};

export default CustomAttributesPanel;
